import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import Table from 'react-bootstrap/Table';
import { useTranslation } from 'react-i18next';
import {find, get, isEmpty, map, pickBy} from 'lodash';
import FontAwesome from 'react-fontawesome';
import { Row, Col } from 'react-bootstrap';
import {CURRENT_STEPS_TABLE_HEADERS_ARRAY} from '../../constants';
import { applySort, statusIconFormatter } from '../../utils/common';

import './index.css';

const CurrentStepsStatisticsScreen = ({statistics, projects}) => {
  const [ t ] = useTranslation();

  const [sortOrder, setSortOrder] = useState("ascending");

  const changeSortOrder = () => {
    setSortOrder(sortOrder === "ascending" ? "descending" : "ascending");
  };

  const sortByProgress = (a, b, data, sortMode) => {
    if(sortMode === "descending") {
      return data[b].progress - data[a].progress;
    }
    return data[a].progress - data[b].progress
  };

  const sortMethods = {
    progress: sortByProgress,
  };

  const [sortMethod, setSortMethod] = useState(() => sortByProgress);

  const headers = [
    t('statisticsTable.headers.project'),
    t('statisticsTable.headers.instruction_notes'),
    t('statisticsTable.headers.in_process'),
    t('statisticsTable.headers.completed'),
    t('statisticsTable.headers.reviewed'),
    t('statisticsTable.headers.locked'),
    t('statisticsTable.headers.all'),
    t('statisticsTable.headers.progress')
  ];

  const sortedStatistics = useMemo(() =>
    applySort(statistics, sortMethod, sortOrder), [statistics, sortMethod, sortOrder]
  );

  const filteredStatistics = useMemo(() =>
    pickBy(sortedStatistics, (value, key) => !!projects.find(project => project.path === key)), [sortedStatistics, projects]
  );

  if (isEmpty(statistics)) {
    return (
        <>{t('currentStepsStatistics.invalidStatistics')}</>
    )
  }

  if (isEmpty(projects)) {
    return (
        <>{t('currentStepsStatistics.projectsNotLoaded')}</>
    )
  }

  const renderInstructionNotes = project => {
    if (project.hasOwnProperty.call(project, 'instruction_notes')) {
      const closedNotes = project.instruction_notes.all_notes - project.instruction_notes.active_notes;
      return (
        <span>{closedNotes}/{project.instruction_notes.all_notes}
          <span className="align-right">
                {closedNotes ? (closedNotes / project.instruction_notes.all_notes * 100).toFixed(1) : '0'}%
          </span>
        </span>
      )
    }
    return null;
  };

  return (
      <Table striped bordered hover>
        <thead>
        <tr>
          {headers.map((header, i) => {
            if (get(sortMethods, CURRENT_STEPS_TABLE_HEADERS_ARRAY[i])) {
              const isSortingOn = sortMethod.toString() === sortMethods[CURRENT_STEPS_TABLE_HEADERS_ARRAY[i]].toString();
              const sortingIcon = sortOrder === "ascending" ? "arrow-down" : "arrow-up";
              const finalIcon = isSortingOn ? sortingIcon : "minus";

              return (
                <th key={CURRENT_STEPS_TABLE_HEADERS_ARRAY[i]}>
                  {header} <FontAwesome
                  className="sortArrow"
                  onClick={() => {
                    changeSortOrder();
                    setSortMethod(() => sortMethods[CURRENT_STEPS_TABLE_HEADERS_ARRAY[i]]);
                  }}
                  name={finalIcon}
                />
                </th>);
              }
            return <th key={CURRENT_STEPS_TABLE_HEADERS_ARRAY[i]}>{statusIconFormatter(header)}</th>
          })}
        </tr>
        </thead>
        <tbody>
          {map(filteredStatistics, (row, key) => (
            <tr key={key}>
              {<td key={key}>{find(projects, project => project.path === key).name}</td>}
              {<td key={`coach${key}`}>
                {renderInstructionNotes(find(projects, project => project.path === key))}
              </td>}
              {map(row, (statistic, statisticKey) => (
                <td key={statisticKey}>
                  {statisticKey === 'progress'
                    ? <Row>
                        <Col style={{display: 'flex', justifyContent: 'center'}}>
                          <span className="text-center progress-bar-text">{statistic}%</span>
                          <div className="progress">
                            <div className={`progress-bar progress-bar-striped bg-${row.progress < 25 ? 'danger' : row.progress < 75 ? 'warning' : 'success'}`}
                              role="progressbar"
                              style={{width: `${row.progress}%`}}
                              aria-valuenow={row.progress}
                              aria-valuemin="0"
                              aria-valuemax="100">
                              <span style={{visibility: 'hidden'}}>{statistic}%</span>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    : statisticKey === 'all'
                      ? statistic
                      : <span>{statistic}/{row.all}<span className="align-right">{(statistic/row.all*100).toFixed(1)}%</span></span>
                  }
                </td>
            ))}
            </tr>
            )
          )}
        </tbody>
      </Table>
  );
};

CurrentStepsStatisticsScreen.propTypes = {
  statistics: PropTypes.object.isRequired,
  projects: PropTypes.array.isRequired,
};

export default CurrentStepsStatisticsScreen;